import { useCallback, useMemo } from 'react'
import { GetFormat, PostFormat, getProvider, postProvider, updateProvider, useGet, usePost } from 'sharedKernel/api'
import { triggerFileDownloadOnClientSide } from 'sharedKernel/file/handler'
import { showToast } from 'components/toast'
import { trans } from 'sharedKernel/i18n'
import { IMeetingRoomSolicitationDetail } from '../types'
import {
  MEETING_ROOM_SOLICITATIONS_PAGE_SIZE,
  RESERVATION_MEETING_ROOM_API,
  ReservationStatusCode,
} from '../api/reservationConstants'
import { buildReservationMeetingRoomListPayload } from '../api/listRequest'
import {
  buildReservationMeetingRoomActionPayload,
  normalizeReservationMeetingRoomDetailResponse,
  normalizeReservationMeetingRoomListResult,
} from '../api/mappers'

export type UseMeetingRoomSolicitationsOptions = {
  searchTerm?: string
  meetingRoomId?: string
  page?: number
  pageSize?: number
}

export const useMeetingRoomSolicitations = ({
  searchTerm = '',
  meetingRoomId,
  page = 1,
  pageSize = MEETING_ROOM_SOLICITATIONS_PAGE_SIZE,
}: UseMeetingRoomSolicitationsOptions = {}) => {
  const listRequest: PostFormat = useMemo(
    () => ({
      url: RESERVATION_MEETING_ROOM_API.BASE,
      data: buildReservationMeetingRoomListPayload({
        searchTerm,
        meetingRoomId,
        page,
        limit: pageSize,
      }),
    }),
    [searchTerm, meetingRoomId, page, pageSize]
  )

  const queryKey = meetingRoomId
    ? `meeting-room-reservations_${meetingRoomId}_${searchTerm}_${page}_${pageSize}`
    : `meeting-room-solicitations_${searchTerm}_${page}_${pageSize}`

  const { data, isLoading, isFetching, refetch } = usePost<unknown>(listRequest, queryKey, {
    enabled: meetingRoomId == null || meetingRoomId !== '',
    keepPreviousData: true,
  })

  const listResult = useMemo(
    () => normalizeReservationMeetingRoomListResult(data),
    [data]
  )

  const solicitations = useMemo(
    (): IMeetingRoomSolicitationDetail[] => listResult.rows,
    [listResult.rows]
  )

  return {
    solicitations,
    totalRows: listResult.total,
    pageSize,
    isLoading,
    isRefetching: isFetching && !isLoading,
    refetch,
  }
}

export const useMeetingRoomSolicitationDetail = (solicitationId: string | null) => {
  const request: GetFormat = {
    url: RESERVATION_MEETING_ROOM_API.byId(solicitationId ?? ''),
  }

  const { data, isFetching } = useGet<unknown>(
    request,
    `meeting-room-solicitation-detail_${solicitationId ?? ''}`,
    { enabled: solicitationId != null && solicitationId !== '' }
  )

  const solicitation = useMemo(
    (): IMeetingRoomSolicitationDetail | null =>
      normalizeReservationMeetingRoomDetailResponse(data),
    [data]
  )

  return {
    solicitation,
    isLoading: isFetching,
  }
}

type SolicitationAction = 'approve' | 'reject' | 'cancel'

const ACTION_STATUS: Record<SolicitationAction, ReservationStatusCode> = {
  approve: ReservationStatusCode.Approved,
  reject: ReservationStatusCode.Rejected,
  cancel: ReservationStatusCode.Cancelled,
}

const ACTION_ENDPOINT: Record<SolicitationAction, (id: string) => string> = {
  approve: RESERVATION_MEETING_ROOM_API.approve,
  reject: RESERVATION_MEETING_ROOM_API.reject,
  cancel: RESERVATION_MEETING_ROOM_API.cancel,
}

export const useMeetingRoomSolicitationActions = (onSuccess?: () => Promise<void>) => {
  const { mutateAsync: updateSolicitation, isLoading: isUpdating } = updateProvider()
  const { mutateAsync: downloadExport, isLoading: isExporting } = postProvider()

  const runAction = useCallback(
    async (
      action: SolicitationAction,
      solicitation: IMeetingRoomSolicitationDetail,
      overrides?: {
        meetingRoomId?: string
        roomName?: string
        startTime?: string
        endTime?: string
        isAdminEdit?: boolean
      }
    ): Promise<void> => {
      const payload = buildReservationMeetingRoomActionPayload(solicitation, {
        status: ACTION_STATUS[action],
        isAdminEdit: overrides?.isAdminEdit ?? false,
        meetingRoomId: overrides?.meetingRoomId,
        roomName: overrides?.roomName,
        startTime: overrides?.startTime,
        endTime: overrides?.endTime,
      })

      await updateSolicitation({
        url: ACTION_ENDPOINT[action](solicitation.id),
        data: payload,
      })

      await onSuccess?.()
    },
    [onSuccess, updateSolicitation]
  )

  const approve = useCallback(
    (solicitation: IMeetingRoomSolicitationDetail) => runAction('approve', solicitation),
    [runAction]
  )

  const reject = useCallback(
    (solicitation: IMeetingRoomSolicitationDetail) => runAction('reject', solicitation),
    [runAction]
  )

  const cancel = useCallback(
    (solicitation: IMeetingRoomSolicitationDetail) => runAction('cancel', solicitation),
    [runAction]
  )

  const edit = useCallback(
    async (
      solicitation: IMeetingRoomSolicitationDetail,
      data: { meetingRoomId: string; roomName: string; startTime: string; endTime: string }
    ): Promise<void> => {
      const payload = buildReservationMeetingRoomActionPayload(solicitation, {
        status: ReservationStatusCode.Approved,
        isAdminEdit: true,
        meetingRoomId: data.meetingRoomId,
        roomName: data.roomName,
        startTime: data.startTime,
        endTime: data.endTime,
      })

      await updateSolicitation({
        url: RESERVATION_MEETING_ROOM_API.update(solicitation.id),
        data: payload,
      })

      await onSuccess?.()
    },
    [onSuccess, updateSolicitation]
  )

  const exportReservations = useCallback(async (meetingRoomId?: string): Promise<void> => {
    try {
      const response = await downloadExport({
        url: RESERVATION_MEETING_ROOM_API.EXPORT,
        data: buildReservationMeetingRoomListPayload({
          meetingRoomId: meetingRoomId,
        }),
        httpConfig: { responseType: 'blob' },
      })
      triggerFileDownloadOnClientSide(response.data, 'reservas-salas.xlsx')
      showToast('success', trans('meetingRoom.reservations.toast.exportSuccess'))
    } catch {
      showToast('error', trans('meetingRoom.reservations.toast.exportError'))
    }
  }, [downloadExport])

  return {
    approve,
    reject,
    cancel,
    edit,
    exportReservations,
    isUpdating,
    isExporting,
  }
}
